// Core types for CX Visual Editor (scaffold)

export type Variant = {
  hierarchy: string;
  locale: string;
  scenario: string;
  /** When hierarchy is Cluster:<reference>, the selected cluster's ID for render/inspector API. */
  clusterId?: string;
};

export type VariantWithTemplate = Variant & {
  templateKey: string;
  fuelType?: string; // Fuel type for Web templates (ELECTRIC, GAS, WATER, DUAL)
};

export type InspectorFieldBase = {
  id: string;
  label: string;
  description?: string;
  fieldName?: string;
  metadata?: any; // For CDF support: tracks original ID and fuel type
};

export type TextField = InspectorFieldBase & {
  value: string;
  maxLength?: number;
  attributes?: string[]; // For CDF support: array of tags like ["CDF_EL_GAS_WATER", "OTHER_TAG"]
  fuelSpecificValues?: Record<string, string>; // For CDF support: { ELECTRIC: "value1", GAS: "value2" }
};
export type ColorField = InspectorFieldBase & { value: string; token?: string; isOverride?: boolean };
export type ImageField = InspectorFieldBase & { value: string; alt?: string };
export type MetadataField = InspectorFieldBase & { type: 'boolean'|'enum'|'number'|'string'; value: any; options?: string[] };

export type InspectorModel = {
  text: TextField[];
  colors: ColorField[];
  images: ImageField[];
  metadata: MetadataField[];
  signedOff?: boolean;
};

export type RenderRequest = {
  templateKey: string;
  fuelType?: string; // Fuel type for Web templates (ELECTRIC, GAS, WATER, DUAL)
  elementKey: string;
  variant: Variant;
  previewMode: 'desktop'|'mobile';
  context?: Record<string, any>;
  values?: Record<string, any>;
  refreshKey?: number; // Optional refresh trigger to force re-render
};

export type RenderResponse = {
  html: string;
  css?: string;
  assets?: Array<{ id: string; url: string }>;
  warnings?: string[];
  auditToken?: string;
};

export type DynamicRenderRequest = {
  projectId: string;
  scenarioId: string;
  uuid?: string;
  sectionNames?: string[]; // Array of technical section names (e.g., ["MONTHLY_SUMMARY_V2"]). Empty array for Full Template.
  productType?: string; // Product type (e.g., "HER" for Home Energy Reports)
  /** For EMAIL channel when cluster chosen: cluster references (same pattern as sectionNames array). Comma-separated on the wire if needed. */
  clusterReferences?: string[];
  /** Locale (e.g. "en_US") sent from FE. */
  locale?: string;
};

export type DynamicRenderResponse = {
  html: string;
  subject: string;
  herPreviewUrl?: string; // HER preview URL to be displayed in iframe
};

export type Channel = 'Email'|'HER'|'Web'|'SMS';

export type TemplateSummary = {
  templateKey: string;
  name: string;
  channel: Channel;
};

export type TemplatesResponse = {
  channels: Array<{ name: Channel; templates: TemplateSummary[] }>;
  requestId?: string;
};

export type CompareDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variantA: VariantWithTemplate | null;
  variantB: VariantWithTemplate | null;
  onChange: (variant: 'A' | 'B', data: VariantWithTemplate) => void;
  onRun: () => void;
  availableTemplates: Array<{ templateKey: string; name: string }>;
  availableScenarios: Array<{ value: string; label: string }>;
  availableClusters?: Array<{ id: string; name: string }>;
};

export type DataScenario = {
  createdAt: string;
  defaultScenario: boolean;
  documentId: string;
  elementNames: string[]; // Array of technical section names (e.g., ["FULL_TEMPLATE", "HEADER_V4", "MONTHLY_SUMMARY_V2"])
  fuelType: string;
  id: number;
  pilotId?: string; // Optional pilot ID
  productType: string;
  publishedAt: string;
  scenarioDisplayName: string;
  scenarioDocumentation?: string; // Optional documentation
  scenarioId: string;
  scenarioLocale: string;
  templateName: string;
  updatedAt: string;
  userClusters: string | null | string[]; // Can be string, null, or array
};

export type DataScenariosResponse = {
  dataScenarios: DataScenario[];
  requestId?: string;
};

// Feature Metric API Types
export type FeatureResolution = {
  clusterReference: string;
  createdAt: number;
  displayName?: string;
  featureId: string;
  featureValueResolutionData: any | null;
  featureValueResolutionType: string;
  level: 'LEVEL_1' | 'LEVEL_2';
  position: number | null;
  product: string;
  resolution: 'ACCESS' | 'KILL';
  updatedAt: number;
  // Level 2 specific fields
  fuelType?: string;
  isMandatory?: boolean;
  productType?: string;
  section?: string;
  metadata?: {
    customerType?: string;
    mappedPage?: string;
    meterType?: string;
    page?: string;
    sections?: string[];
    event?: string;
    fuelType?: string;
    [key: string]: any; // Allow other metadata fields
  };
};

export type FeatureMetricResponse = {
  clusterPriority: any | null;
  featureResolutions: FeatureResolution[];
};

// --- Preview-to-Inspector Hover Matching Types ---

export type DataAttrConfigRef = { type: string; key: string };

export type DataAttrHints = {
  textIds: string[];    // configKey of non-palette data-configs entries
  stringIds: string[];  // data-strings entries that are real resource IDs (not "[value of ...]")
  colorIds: string[];   // configKey of palette data-configs entries + data-colors values
};

export type PreviewHoverPayload = {
  channel: 'Email' | 'Web' | 'HER';
  colorHex?: string | null;
  backgroundHex?: string | null;
  source: 'email-desktop' | 'web-desktop' | 'her-desktop';
  dataAttrHints?: DataAttrHints | null;
};

export type InspectorHoverMatch = {
  colorMatch?: {
    id: string;
    score: number;
    reason: 'exact-hex' | 'near-hex' | 'direct';
  } | null;
  bgColorMatch?: {
    id: string;
    score: number;
    reason: 'exact-hex' | 'near-hex';
  } | null;
  /** All text field IDs matched via data-attrs (for multi-field highlighting). */
  directMatchIds?: string[] | null;
};

// --- Preview Export Types ---

export type ExportAction = 'download' | 'clipboard';

export type ExportTarget =
  | 'email-desktop'
  | 'email-mobile'
  | 'web-desktop'
  | 'web-mobile'
  | 'her';

export type ExportFailureReason =
  | 'unsupported'
  | 'not-ready'
  | 'capture-failed'
  | 'clipboard-denied';

export type ExportResult =
  | { ok: true; filename?: string }
  | { ok: false; reason: ExportFailureReason };

export type PreviewExportHandle = {
  exportPreview: (action: ExportAction) => Promise<ExportResult>;
};

export const SUPPORTED_EXPORT_CHANNELS: readonly Channel[] = ['Email', 'Web', 'HER'];

export type ExportInProgressAction = ExportAction | null;



